
import { useEffect, useState } from "react"
import { useForm } from "react-hook-form"
import { zodResolver } from "@hookform/resolvers/zod"
import * as z from "zod"
import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
    DialogFooter,
} from "@/components/ui/dialog"
import {
    Form,
    FormControl,
    FormField,
    FormItem,
    FormLabel,
    FormMessage,
} from "@/components/ui/form"
import { Input } from "@/components/ui/input"
import { Button } from "@/components/ui/button"
import { Textarea } from "@/components/ui/textarea"
import { VideoSelect } from "@/components/courses/VideoSelect"
import { courseService } from "@/services/course.service"
import { useToast } from "@/hooks/use-toast"
import type { Lesson } from "@/types/api"
import { Checkbox } from "@/components/ui/checkbox"
import { Loader2 } from "lucide-react"

const formSchema = z.object({
    title: z.string().min(2, "Название должно содержать не менее 2 символов"),
    description: z.string().optional(),
    video_id: z.string().optional(), // We handle ID as string in form, convert to number for API
    is_active: z.boolean().default(true),
    order: z.number().int().default(1),
})

interface LessonDialogProps {
    lesson: Lesson | null
    courseId: number
    open: boolean
    onOpenChange: (open: boolean) => void
    onSuccess: () => void
}

export function LessonDialog({
    lesson,
    courseId,
    open,
    onOpenChange,
    onSuccess,
}: LessonDialogProps) {
    const { toast } = useToast()
    const [isLoading, setIsLoading] = useState(false)
    const form = useForm<z.infer<typeof formSchema>>({
        resolver: zodResolver(formSchema),
        defaultValues: {
            title: "",
            description: "",
            video_id: "",
            is_active: true,
            order: 1,
        },
    })

    useEffect(() => {
        if (lesson) {
            // Find existing video ID if any
            const vidId = lesson.video ? String(lesson.video.id) : ""
            form.reset({
                title: lesson.title,
                description: lesson.description || "",
                video_id: vidId,
                is_active: lesson.is_active,
                order: lesson.order,
            })
        } else {
            form.reset({
                title: "",
                description: "",
                video_id: "",
                is_active: true,
                order: 1,
                // Order should probably be auto-calculated? We'll leave as 1 for now or pass maxOrder
            })
        }
    }, [lesson, form, open])

    const onSubmit = async (values: z.infer<typeof formSchema>) => {
        if (!courseId) return

        setIsLoading(true)
        try {
            const payload = {
                title: values.title,
                description: values.description || "",
                video_id: values.video_id ? parseInt(values.video_id) : null,
                is_active: values.is_active,
                order: values.order,
                course: courseId,
            } as any // Cast because createLesson payload is specific

            if (lesson) {
                await courseService.updateLesson(lesson.id, payload)
                toast({
                    title: "Успешно",
                    description: "Урок успешно обновлен",
                })
            } else {
                await courseService.createLesson(payload)
                toast({
                    title: "Успешно",
                    description: "Урок успешно создан",
                })
            }
            onSuccess()
            onOpenChange(false)
        } catch (error) {
            console.error(error)
            toast({
                title: "Ошибка",
                description: `Не удалось ${lesson ? "обновить" : "создать"} урок`,
                variant: "destructive",
            })
        } finally {
            setIsLoading(false)
        }
    }

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent className="sm:max-w-[500px]">
                <DialogHeader>
                    <DialogTitle>{lesson ? "Редактировать урок" : "Создать урок"}</DialogTitle>
                </DialogHeader>
                <Form {...form}>
                    <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
                        <FormField
                            control={form.control}
                            name="title"
                            render={({ field }) => (
                                <FormItem>
                                    <FormLabel>Название</FormLabel>
                                    <FormControl>
                                        <Input {...field} />
                                    </FormControl>
                                    <FormMessage />
                                </FormItem>
                            )}
                        />
                        <FormField
                            control={form.control}
                            name="description"
                            render={({ field }) => (
                                <FormItem>
                                    <FormLabel>Описание</FormLabel>
                                    <FormControl>
                                        <Textarea {...field} />
                                    </FormControl>
                                    <FormMessage />
                                </FormItem>
                            )}
                        />
                        <FormField
                            control={form.control}
                            name="video_id"
                            render={({ field }) => (
                                <FormItem>
                                    <FormLabel>Видео контент</FormLabel>
                                    <FormControl>
                                        <VideoSelect value={field.value} onChange={field.onChange} />
                                    </FormControl>
                                    <FormMessage />
                                </FormItem>
                            )}
                        />
                        <FormField
                            control={form.control}
                            name="order"
                            render={({ field }) => (
                                <FormItem>
                                    <FormLabel>Порядок</FormLabel>
                                    <FormControl>
                                        <Input type="number" {...field} onChange={e => field.onChange(parseInt(e.target.value))} />
                                    </FormControl>
                                    <FormMessage />
                                </FormItem>
                            )}
                        />
                        <FormField
                            control={form.control}
                            name="is_active"
                            render={({ field }) => (
                                <FormItem className="flex flex-row items-start space-x-3 space-y-0 rounded-md border p-4">
                                    <FormControl>
                                        <Checkbox
                                            checked={field.value}
                                            onCheckedChange={field.onChange}
                                        />
                                    </FormControl>
                                    <div className="space-y-1 leading-none">
                                        <FormLabel>
                                            Активен
                                        </FormLabel>
                                    </div>
                                </FormItem>
                            )}
                        />
                        <DialogFooter>
                            <Button type="submit" disabled={isLoading}>
                                {isLoading ? (
                                    <>
                                        <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                                        {lesson ? "Сохранение..." : "Создание..."}
                                    </>
                                ) : (
                                    lesson ? "Сохранить изменения" : "Создать"
                                )}
                            </Button>
                        </DialogFooter>
                    </form>
                </Form>
            </DialogContent>
        </Dialog>
    )
}
